import React from 'react';
import axios from 'axios';
import Button from '../../components/Button';

// Daily Digest — a once-a-day plain-text rollup of the last 24h of alerts and
// fleet posture. A provider brief (every client; the operator's morning brief)
// goes to the recipients below; each client company can also get its own scoped
// digest. Self-contained: talks to /api/digest-* directly. Provider-only on the
// backend; renders nothing if the caller isn't allowed.

const fmtWhen = (iso) => {
	if (!iso) return '—';
	try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' };

const DigestSettings = ({ styles }) => {
	const [form, setForm] = React.useState(null);
	const [subs, setSubs] = React.useState([]);
	const [meta, setMeta] = React.useState({ emailEnabled: true, fallbackRecipient: '', lastRun: null });
	const [loading, setLoading] = React.useState(true);
	const [busy, setBusy] = React.useState(false);
	const [toast, setToast] = React.useState({ type: '', message: '' });
	const [preview, setPreview] = React.useState(null);

	const load = React.useCallback(async () => {
		try {
			const { data } = await axios.get('/api/digest-settings');
			if (data && data.status) {
				setForm({
					DigestEnabled: !!data.DigestEnabled,
					DigestHour: String(data.DigestHour ?? '7'),
					DigestRecipients: data.DigestRecipients || '',
					DigestOfflineMinutes: String(data.DigestOfflineMinutes ?? '20'),
				});
				setSubs(data.subscriptions || []);
				setMeta({
					emailEnabled: !!data.emailEnabled,
					fallbackRecipient: data.fallbackRecipient || '',
					lastRun: data.lastRun || null,
				});
			}
		} catch (e) { /* not a provider, or request failed — render nothing */ }
		setLoading(false);
	}, []);

	React.useEffect(() => { load(); }, [load]);

	const setStr = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
	const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === 'true' }));

	const post = React.useCallback(async (url, payload, okMsg, after) => {
		setBusy(true);
		setToast({ type: '', message: '' });
		try {
			const { data } = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
			if (data && data.status) {
				setToast({ type: 'success', message: data.msg || okMsg });
				if (after) after(data);
			} else {
				setToast({ type: 'error', message: (data && data.msg) || 'Request failed' });
			}
			return data;
		} catch (e) {
			setToast({ type: 'error', message: e?.response?.data?.msg || e?.message || 'Request failed' });
			return null;
		} finally {
			setBusy(false);
		}
	}, []);

	const runPreview = (companyId) => post('/api/digest-run', { action: 'preview', companyId }, '',
		(d) => setPreview({ subject: d.subject, html: d.html, body: d.body }));
	const runSend = async (companyId) => { await post('/api/digest-run', { action: 'send', companyId }, 'Digest sent'); await load(); };

	const setSubField = (companyId, k, v) => setSubs((rows) =>
		rows.map((r) => (r.companyId === companyId ? { ...r, [k]: v } : r)));
	const saveSub = async (row) => {
		await post('/api/digest-subscription',
			{ companyId: row.companyId, enabled: row.enabled, recipients: row.recipients },
			'Subscription saved', (d) => setSubs(d.subscriptions || []));
	};

	if (loading || !form) return null;

	// Compact override for table-row action buttons — the shared Button class has
	// 1rem 5rem padding (≈180px wide), which stacks three to a cell; shrink them so
	// Save/Preview/Send sit on one line like the top action row.
	const rowBtn = { padding: '6px 12px', fontSize: '1.1rem' };
	const field = { display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 180px', minWidth: 150 };
	const labelSm = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
	const inputCss = {
		padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box',
		background: 'var(--input-bg, var(--card-bg, rgba(0,0,0,.15)))', color: 'var(--text-primary)',
		border: '1px solid var(--border-color)', borderRadius: 8,
	};

	return (
		<div style={{ marginTop: 28, borderTop: '1px solid var(--border-color)', paddingTop: 20, maxWidth: 980 }}>
			<h2 style={{ marginBottom: 4 }}>Daily Digest</h2>
			<p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 0, maxWidth: 760 }}>
				A once-a-day email summarising the last 24 hours — new alerts, what needs attention, and
				fleet health. The <strong>provider brief</strong> covers every client (your morning brief); each
				client can also get its own scoped digest below.
			</p>

			{!meta.emailEnabled && (
				<div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8,
					background: 'rgba(192,20,18,.1)', border: '1px solid rgba(192,20,18,.35)', color: 'var(--text-primary)' }}>
					<strong>Notification email is off.</strong> The digest will not send until email is enabled and a
					provider is configured on this Settings page.
				</div>
			)}

			{/* Status */}
			<div style={{ margin: '14px 0' }}>
				<div style={label}>Last provider brief</div>
				<div style={{ fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)' }}>{fmtWhen(meta.lastRun?.at)}</div>
				<div style={{ fontSize: 12, color: meta.lastRun ? (meta.lastRun.status === 'sent' ? 'var(--green,#12c077)' : 'var(--primary-color)') : 'var(--text-secondary)' }}>
					{meta.lastRun ? `${meta.lastRun.status} · ${meta.lastRun.sent || 0} sent${meta.lastRun.failed ? `, ${meta.lastRun.failed} failed` : ''}` : 'Not sent yet'}
				</div>
			</div>

			{/* Global config — explicit flex row so nothing stretches */}
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
				<label style={field}>
					<span style={labelSm}>Daily digest</span>
					<select style={inputCss} value={String(form.DigestEnabled)} onChange={setBool('DigestEnabled')}>
						<option value='true'>Enabled</option>
						<option value='false'>Disabled</option>
					</select>
				</label>
				<label style={field}>
					<span style={labelSm}>Send hour (0-23)</span>
					<input type='number' min='0' max='23' style={inputCss} value={form.DigestHour} onChange={setStr('DigestHour')} />
				</label>
				<label style={field}>
					<span style={labelSm}>Offline after (min)</span>
					<input type='number' min='1' style={inputCss} value={form.DigestOfflineMinutes} onChange={setStr('DigestOfflineMinutes')} />
				</label>
				<label style={{ ...field, flex: '1 1 300px', minWidth: 260 }}>
					<span style={labelSm}>Provider brief recipients (comma-separated)</span>
					<input style={inputCss}
						placeholder={meta.fallbackRecipient ? `defaults to ${meta.fallbackRecipient}` : 'you@company.com, soc@company.com'}
						value={form.DigestRecipients} onChange={setStr('DigestRecipients')} />
				</label>
			</div>
			<p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
				Leave recipients blank to use the notification Recipient configured above{meta.fallbackRecipient ? ` (${meta.fallbackRecipient})` : ''}.
			</p>

			<div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
				<Button onClick={() => post('/api/digest-settings', { ...form }, 'Digest settings saved', (d) => { setSubs(d.subscriptions || []); })} disabled={busy}>Save</Button>
				<Button onClick={() => runPreview(null)} disabled={busy}>Preview brief</Button>
				<Button onClick={() => runSend(null)} disabled={busy || !meta.emailEnabled}>Send brief now</Button>
			</div>

			{/* Per-client subscriptions */}
			<div style={{ marginTop: 24 }}>
				<div style={{ ...label, marginBottom: 6 }}>Per-client digests ({subs.length})</div>
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
						<thead>
							<tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px' }}>
								<th style={{ padding: '6px 10px' }}>Client</th>
								<th style={{ padding: '6px 10px', width: 40, textAlign: 'center' }}>On</th>
								<th style={{ padding: '6px 10px' }}>Recipients (comma-separated)</th>
								<th style={{ padding: '6px 10px', width: 250 }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{subs.map((row) => (
								<tr key={row.companyId} style={{ borderTop: '1px solid var(--border-color)' }}>
									<td style={{ padding: '6px 10px', whiteSpace: 'nowrap', fontWeight: 600 }}>{row.company}</td>
									<td style={{ padding: '6px 10px', textAlign: 'center' }}>
										<input type='checkbox' checked={!!row.enabled}
											onChange={(e) => setSubField(row.companyId, 'enabled', e.target.checked)} />
									</td>
									<td style={{ padding: '6px 10px' }}>
										<input
											style={{ ...inputCss, padding: '6px 8px' }}
											placeholder='client@example.com'
											value={row.recipients || ''}
											onChange={(e) => setSubField(row.companyId, 'recipients', e.target.value)} />
									</td>
									<td style={{ padding: '6px 10px' }}>
										<div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
											<Button style={rowBtn} onClick={() => saveSub(row)} disabled={busy}>Save</Button>
											<Button style={rowBtn} onClick={() => runPreview(row.companyId)} disabled={busy}>Preview</Button>
											<Button style={rowBtn} onClick={() => runSend(row.companyId)} disabled={busy || !meta.emailEnabled || !row.enabled}>Send</Button>
										</div>
									</td>
								</tr>
							))}
							{subs.length === 0 && (
								<tr><td colSpan={4} style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>No client companies.</td></tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{toast.message && (
				<div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8,
					background: toast.type === 'error' ? 'rgba(192,20,18,.1)' : 'rgba(18,192,119,.12)',
					border: `1px solid ${toast.type === 'error' ? 'rgba(192,20,18,.35)' : 'rgba(18,192,119,.4)'}`,
					color: 'var(--text-primary)' }}>
					{toast.message}
				</div>
			)}

			{preview && (
				<div style={{ marginTop: 16 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<div style={label}>Preview — {preview.subject}</div>
						<Button onClick={() => setPreview(null)} disabled={busy}>Close</Button>
					</div>
					{preview.html ? (
						<iframe
							title='digest-preview'
							srcDoc={preview.html}
							style={{ marginTop: 6, width: '100%', height: 560, border: '1px solid var(--border-color)',
								borderRadius: 8, background: '#fff' }} />
					) : (
						<pre style={{ marginTop: 6, padding: 14, borderRadius: 8, overflowX: 'auto',
							background: 'var(--card-bg, rgba(0,0,0,.25))', border: '1px solid var(--border-color)',
							color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', fontSize: 12, lineHeight: 1.5 }}>
							{preview.body}
						</pre>
					)}
				</div>
			)}
		</div>
	);
};

export default DigestSettings;
